import { cn } from '@sitewise/ui';

/**
 * Compact segmented control used by the unit Lead tab to pick
 * friction / exemption / test-result values. Pure presentational —
 * the parent owns the value and the save lifecycle.
 *
 * Visually matches the design-system tabs but is rendered as a row
 * of pill buttons because the underlying meaning is a single-choice
 * radio, not a navigation tab.
 */
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Optional short helper, shown beneath the row when this option
   *  is selected (e.g. "Approved letter required"). */
  hint?: string;
}

interface Props<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: ReadonlyArray<SegmentedOption<T>>;
  disabled?: boolean;
  ariaLabel: string;
  className?: string;
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  disabled,
  ariaLabel,
  className,
}: Props<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex h-9 items-center rounded-md border border-border bg-surface p-0.5 text-sm',
        disabled && 'opacity-60',
        className,
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => {
              if (!selected) onChange(opt.value);
            }}
            className={cn(
              'inline-flex h-7 items-center rounded px-3 text-xs font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              selected
                ? 'bg-primary text-primary-fg shadow-sm'
                : 'text-fg-secondary hover:bg-surface-muted',
              disabled && 'cursor-not-allowed',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
